import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { healthCommitteeApi } from "../../services/healthCommitteeApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const STATUSES = ["DRAFT", "REVIEW_REQUIRED", "SUBMITTED", "APPROVED", "FINALIZED", "REOPENED", "REPLACED", "CANCELLED"];

export default function HealthCommitteeMeetingsPage() {
  const { t } = useLocalization();
  const { canViewHealthCommittee, canManageHealthCommittee } = usePermissions();
  const [meetings, setMeetings] = useState([]);
  const [status, setStatus] = useState("");

  const load = useCallback(() => {
    healthCommitteeApi.listMeetings(status ? { status } : {}).then((r) => setMeetings(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { if (canViewHealthCommittee) load(); }, [canViewHealthCommittee, load]);

  if (!canViewHealthCommittee) return <p className="text-sm text-gray-500" data-testid="health-committee-access-denied">{t("common.accessDenied")}</p>;

  return (
    <div className="space-y-5" data-testid="health-committee-meetings-page">
      <PageHelpBanner pageKey="health-committee-meetings" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtHealthCommittee.meetingsHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/management/compliance/health-committee" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="health-committee-back-link">&larr; {t("mgmtHealthCommittee.backToDashboard")}</Link>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-1">{t("mgmtHealthCommittee.meetings")}</h2>
        </div>
        {canManageHealthCommittee && (
          <Link to="/management/compliance/health-committee/meetings/new" data-testid="health-committee-new-meeting-link" className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">
            + {t("mgmtHealthCommittee.newMeeting")}
          </Link>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button data-testid="health-committee-status-filter-ALL" onClick={() => setStatus("")} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${!status ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>{t("mgmtLongHourInterview.all")}</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`health-committee-status-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${status === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("common.date")}</th><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colType")}</th><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colLocation")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {meetings.map((m) => (
              <tr key={m.id} className="border-t border-gray-100" data-testid={`health-committee-meeting-row-${m.id}`}>
                <td className="px-4 py-2 text-gray-700">{m.meeting_date}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{m.meeting_type}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{m.location || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-2"><Link to={`/management/compliance/health-committee/meetings/${m.id}`} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`health-committee-meeting-view-${m.id}`}>{t("mgmtLongHourInterview.view")}</Link></td>
              </tr>
            ))}
            {meetings.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-health-committee-meetings">{t("mgmtHealthCommittee.noMeetingsFilter")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
